import PropTypes from 'prop-types';
import React, {
    createContext,
    useContext,
    useEffect,
    useState
} from 'react';

import { useInfiniteScroll } from '$hooks/ajax-listing';
import { useLocale } from '$contexts/locale.jsx';

const AjaxListingContext = createContext({});
AjaxListingContext.displayName = 'AjaxListingContext';

/* ========================================================================= */
/* Components */
/* ========================================================================= */
const Marker = ({ colour = '#FC5200' }) => {
    return (
        <svg
            className='inline'
            style={{ width: '8px', height: '8px' }}
            viewBox='0 0 8 8'
            xmlns='http://www.w3.org/2000/svg'>
            <circle
                cx='4'
                cy='4'
                fill={colour}
                r='4' />
        </svg>
    );
};

/**
 * @component NextButton
 * @props (see propTypes)
 */
const AjaxListingNextButton = (props) => {
    const { page, setPage, totalPages } = useContext(AjaxListingContext);

    return (
        <button
            aria-label='Next page'
            className={props.className}
            disabled={totalPages <= page}
            onClick={setPage.bind(this, page + 1)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @component PreviousButton
 * @props (see propTypes)
 */
const AjaxListingPreviousButton = (props) => {
    const { page, setPage } = useContext(AjaxListingContext);

    return (
        <button
            aria-label='Previous page'
            className={props.className}
            disabled={page <= 1}
            onClick={setPage.bind(this, page - 1)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @component PageButton
 * @props (see propTypes)
 */
const AjaxListingPageButton = (props) => {
    const { page, setPage } = useContext(AjaxListingContext);

    return (
        <button
            aria-label={`page ${props.page}`}
            className={props.className}
            disabled={page === props.page}
            onClick={setPage.bind(this, props.page)}
            type='button'>
            {props.children}
        </button>
    );
};


/**
 * @component Pagination
 * @props (see propTypes)
 */
const AjaxListingPagination = ({current, totalPages}) => {
    const [showAllPages, setShowAllPages] = useState(totalPages <= 4);

    const { translate: t } = useLocale();

    useEffect(() => {
        setShowAllPages(totalPages <=4);
    }, [totalPages]);

    return (
        showAllPages ? (
            <>
                {[...Array(totalPages).keys()].map((pageNum, idx) => {
                    return (<AjaxListingPageButton
                        className='mr-30 type-style-eyebrow font-mono'
                        key={idx}
                        page={pageNum + 1}>
                        {pageNum + 1}
                        {current === pageNum +1 && (
                            <div className='mt-5'>
                                <Marker />
                            </div>
                        )}
                    </AjaxListingPageButton>);
                }
                )}
            </>
        ) : (
            <>
                {[...Array(4).keys()].map((pageNum, idx) => {
                    return (
                        <AjaxListingPageButton
                            className='mr-30 type-style-eyebrow font-mono'
                            key={idx}
                            page={pageNum + 1}>
                            {pageNum + 1}
                            {current === pageNum +1 && (
                                <div className='mt-5'>
                                    <Marker />
                                </div>
                            )}
                        </AjaxListingPageButton>);
                })}
                <span className='mr-30 type-style-eyebrow font-mono'>...</span>
                <button
                    onClick={() => setShowAllPages(true)}
                    type='button'>
                    <p className='font-mono type-style-eyebrow'>
                        {t('Next')}
                    </p>
                </button>
            </>
        )
    );
};

/**
 * @component InfinitePagination
 * @props (see propTypes)
 */
const InfinitePagination = (props) => {
    const {
        loading,
        page,
        setPage,
        totalPages
    } = useContext(AjaxListingContext);

    const hasNextPage = totalPages > page;

    const infiniteRef = useInfiniteScroll({
        loading: loading,
        hasNextPage: hasNextPage,
        onLoad: () => {
            setPage(page + 1);
        }
    });

    if (hasNextPage || loading) {
        return (
            <div
                aria-label='Load more'
                className={props.className}
                ref={infiniteRef}
                tabIndex='0'>
                {props.children}
            </div>
        );
    };

    return null;
};

/* ========================================================================= */
/* Typing */
/* ========================================================================= */

AjaxListingNextButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string
};

AjaxListingPreviousButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string
};

AjaxListingPageButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string,
    page: PropTypes.number.isRequired
};

AjaxListingPagination.propTypes = {
    current: PropTypes.number,
    totalPages: PropTypes.number,
};

InfinitePagination.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string
};

Marker.propTypes = {
    colour: PropTypes.string
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export {
    AjaxListingContext,
    AjaxListingNextButton,
    AjaxListingPageButton,
    AjaxListingPreviousButton,
    AjaxListingPagination,
    InfinitePagination
};
